/**
 * DataShield
 *
 * Wraps sensitive data sections with client-side friction:
 *   • user-select: none  (prevents text selection & Ctrl+C copy)
 *   • right-click blocked (no "Inspect Element" shortcut on data)
 *   • Ctrl/Cmd + A/C/S/U swallowed inside the wrapper
 *   • ::selection made transparent via className
 *
 * NOTE: This is UX-layer friction — a determined user with DevTools
 * can still read the DOM.  True protection lives in the API:
 *   authenticated-only endpoints, rate limiting, name masking, and
 *   short-lived JWTs.  These client measures raise the effort bar
 *   for casual scraping.
 */
import { useRef, type ReactNode, type KeyboardEvent, type MouseEvent } from 'react';

interface DataShieldProps {
  children: ReactNode;
  className?: string;
  /** Set to false to disable protection (e.g. for admin views where copying is legitimate) */
  active?: boolean;
}

export default function DataShield({ children, className = '', active = true }: DataShieldProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleContextMenu = (e: MouseEvent) => {
    if (active) e.preventDefault();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!active) return;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && ['a', 'c', 's', 'u'].includes(e.key.toLowerCase())) {
      e.preventDefault();
    }
  };

  return (
    <div
      ref={ref}
      role="presentation"
      className={`${active ? 'wits-protected' : ''} ${className}`}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      {children}
    </div>
  );
}
